import { type InfiniteData, type UseInfiniteQueryOptions, type UseQueryOptions } from '@tanstack/react-query'
import { getSurveys, getSurveysStats } from './api'
import type { GetSurveyResponse, GetSurveysParams, GetSurveyStatsResponse } from './types'
import { useInfiniteQuery, useQuery } from '@/lib/use-query'
import { useApiUrl } from '@/hooks/useApiUrl'

export const surveyQueriesFactory = {
	all: () => ['surveys'] as const,
	paginated: ({ perPage, orderBy, direction, state }: Omit<GetSurveysParams, 'page'>) =>
		[...surveyQueriesFactory.all(), { perPage, state, orderBy, direction }] as const,
	stats: () => [...surveyQueriesFactory.all(), 'stats'] as const
}

export function useSurveys({
	page = 1,
	perPage = 5,
	orderBy,
	direction,
	state,
	...queryOpts
}: GetSurveysParams &
	Omit<
		UseInfiniteQueryOptions<GetSurveyResponse, Error, InfiniteData<GetSurveyResponse>>,
		'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
	>) {
	const apiUrl = useApiUrl()

	return useInfiniteQuery({
		...queryOpts,
		queryKey: surveyQueriesFactory.paginated({ perPage, state, orderBy, direction }),
		queryFn: ({ pageParam }) => {
			return getSurveys(apiUrl, { page: pageParam as number, perPage, state, orderBy, direction })
		},
		initialPageParam: page,
		getNextPageParam: lastPage => {
			return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined
		}
	})
}

export function useSurveyStats(queryOpts?: Omit<UseQueryOptions<GetSurveyStatsResponse>, 'queryKey' | 'queryFn'>) {
	const apiUrl = useApiUrl()

	return useQuery({
		...queryOpts,
		queryKey: surveyQueriesFactory.stats(),
		queryFn: () => getSurveysStats(apiUrl)
	})
}
